import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, Download, Paperclip } from "lucide-react";
import { downloadFile, previewUrl } from "@/lib/upload";

// Formats supported for direct browser preview
const IMG_EXT = /\.(png|jpe?g|webp|gif|bmp|svg)$/i;
const PDF_EXT = /\.pdf$/i;

function extOf(name = "", path = "") {
  return (name || path || "").split("?")[0];
}

/**
 * Standalone Preview modal for an attachment {path, name}.
 * Reuses existing upload/download infrastructure — does not duplicate files.
 */
export default function AttachmentPreview({ value, trigger }) {
  const [open, setOpen] = useState(false);
  if (!value?.path) return null;
  const url = previewUrl(value.path);
  const target = extOf(value.name, value.path);
  const isImg = IMG_EXT.test(target);
  const isPdf = PDF_EXT.test(target);

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: (e) => { e.stopPropagation?.(); setOpen(true); } })
      ) : (
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)} title="Preview" data-testid="attachment-preview-btn">
          <Eye className="w-4 h-4" />
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Paperclip className="w-4 h-4 text-[#00A651]" />
              <span className="truncate max-w-xl">{value.name || "Lampiran"}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="bg-slate-100 min-h-[60vh] max-h-[70vh] overflow-auto flex items-center justify-center">
            {isPdf ? (
              <iframe src={url} title={value.name || "PDF"} className="w-full h-[70vh] bg-white" data-testid="attachment-preview-pdf" />
            ) : isImg ? (
              <img src={url} alt={value.name || "Preview"} className="max-w-full max-h-[70vh] object-contain" data-testid="attachment-preview-image" />
            ) : (
              <div className="text-center p-8 space-y-3" data-testid="attachment-preview-unsupported">
                <div className="text-4xl">📄</div>
                <div className="text-sm font-medium text-slate-800">File tidak dapat di-preview pada browser</div>
                <div className="text-xs text-slate-500">Silakan unduh untuk membuka: {value.name}</div>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Tutup</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={() => downloadFile(value.path, value.name)} data-testid="attachment-preview-download">
              <Download className="w-4 h-4 mr-1" /> Unduh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
